import { useAsyncExecutor } from "async-executor-hook";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  FormArray,
  FormControl,
  FormGroup,
  useFormConfig,
  useFormGroup,
  WForm,
  WFormControl,
} from "rectangular-forms";
import { of, tap } from "rxjs";

import { Authorization } from "../../common/AuthorizationCpt";
import { IconNotification } from "../../common/components/IconNotification";
import IconCart from "../../images/cart.svg";
import LoadingSpinner from "../../images/loading_gray.gif";
import IconServiceMore from "../../images/icon_service_more.svg";
import { HTTP_METHODS } from "../../utils/constants";
import { networkBackendRequest } from "../../utils/networkUtils";
import { VolanteAdditionalServicesCart } from "./VolanteAdditionalServicesCart";
import { VolanteAdditionalServicesTable } from "./VolanteAdditionalServicesTable";
import { ButtonAditionUp, Container } from "./VolanteServiceSidebarStyles";
import { TemplateInputContainer } from "../../common/components/TemplateContainer/TemplateContainer";
import { WInputText } from "../../common/components/TemplateContainer/InputTemplates";
import { StatusCommon } from "../../common/components/StatusCommon/StatusCommon";

export const VolanteAdditionalServices = (props) => {
  const toast = useRef();

  const { additionalServices, volante } = props;
  const { status2 } = volante;
  const { problems } = status2;

  const numberProblems = problems.length;

  const text =
    numberProblems === 0
      ? "SIN RESTRICCIONES"
      : `${numberProblems} RESTRICCI${numberProblems === 1 ? "ÓN" : "ONES"}`;

  const { importer } = volante;

  const shoppingCartState = useState([]);
  const [shoppingCart, setShoppingCart] = shoppingCartState;

  const convertedAdditionalServices = additionalServices.map((service) => ({
    amount: service.amount,
    service_name: service.description,
    sap_code: service.sap_code,
  }));

  const [initialSelectedServices] = useState(convertedAdditionalServices);

  const selectedState = useState(convertedAdditionalServices);
  const [, setSelected] = selectedState;

  const tableFormGroup: any = useFormGroup();

  const selectCbk = (amount, info) => (selected) => {
    if (amount < 0) {
      return selected;
    }

    if (amount === 0) {
      return selected.filter((s) => {
        return s.sap_code !== info.sap_code;
      });
    }

    const foundIndex = selected.findIndex(
      (service) => service.sap_code === info.sap_code
    );
    return foundIndex < 0
      ? [
          ...selected,
          {
            sap_code: info.sap_code,
            service_name: info.service_name,
            amount,
          },
        ]
      : selected.map((service, index) => {
          return index !== foundIndex
            ? service
            : {
                sap_code: info.sap_code,
                service_name: info.service_name,
                amount,
              };
        });
  };

  const searchAdditionalServices = useAsyncExecutor(
    (params: any) => {
      return networkBackendRequest(`/additional-services/`, HTTP_METHODS.GET, {
        service_name: params.input || "",
      }).then((resp) => resp.json());
    },
    {
      source$: of({}),
      onSuccess: (data) => {
        const services = data.additional_services.map((info) => {
          const found = initialSelectedServices.find(
            (selected) => selected.sap_code === info.sap_code
          );

          const max_value = info.max_value;

          const amount = found ? found.amount : 0;

          return {
            amount: 0,
            info: {
              ...info,
              max_value: found ? max_value - found.amount : max_value,
              original_max_value: max_value,
              amount,
              checkable: amount < max_value,
            },
          };
        });

        const rowFormGroups = services.map(() => {
          const rowFormGroup = new FormGroup({
            amount: new FormControl(),
            info: new FormControl(),
          });

          rowFormGroup
            .get("amount")
            .valueChanges.pipe(
              tap((amount) => {
                const info = rowFormGroup.get("info").value;
                setSelected(selectCbk(amount, info));
                setShoppingCart(selectCbk(amount, info));
              })
            )
            .subscribe();

          return rowFormGroup;
        });
        const formGroup = new FormGroup({
          services: new FormArray(rowFormGroups, [
            (control) => {
              const rows = control.value;
              const valid = rows.some((service) => service.amount > 0);
              return !valid ? { notServiceSelected: true } : null;
            },
          ]),
        });

        formGroup
          .get("services")
          .valueChanges.pipe(tap(() => {}))
          .subscribe();

        formGroup.patchValue({ services }, { emitEvent: false });
        tableFormGroup.setFormGroup(formGroup);

        setShoppingCart([]);
      },
    }
  );

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const onClose = () => {
    setShowConfirmationModal(!showConfirmationModal);
  };

  const isInvalid = tableFormGroup.getFormGroup().invalid;

  const additionalServicesForSave = useMemo(() => {
    const newAddedServices = shoppingCart
      .filter(
        (s) => !initialSelectedServices.find((i) => i.sap_code === s.sap_code)
      )
      .map((s) => ({ ...s, service_name: undefined }));
    const alreadyAddedServices = initialSelectedServices.map((service) => {
      const found = shoppingCart.find((s) => s.sap_code === service.sap_code);
      return {
        ...service,
        service_name: undefined,
        amount: service.amount + (found ? found.amount : 0),
      };
    });
    return [...newAddedServices, ...alreadyAddedServices];
  }, [shoppingCart, initialSelectedServices]);

  const formConfig = useFormConfig({
    createForm: () => {
      const form = new FormGroup({
        input: new FormControl(),
      });
      return form;
    },
    onSubmit: (control) => {
      searchAdditionalServices.execute(control.value);
    },
  });

  const formConfigLoadSucceed = formConfig.loadSucceed;
  useEffect(() => {
    formConfigLoadSucceed(null);
  }, [formConfigLoadSucceed]);

  return (
    <div>
      <Toast ref={toast}> </Toast>
      <Sidebar
        position="right"
        style={{
          overflowX: "clip",
          width: "65rem",
        }}
        className="volante-additional-services-cart"
        visible={showConfirmationModal}
        onHide={() => {
          setShowConfirmationModal(false);
        }}
      >
        <VolanteAdditionalServicesCart
          selectedState={shoppingCartState}
          {...props}
          onClose={onClose}
          onRemoveService={(service) => {
            tableFormGroup
              .getFormGroup()
              .get("services")
              .controls.filter(
                (serviceFG) =>
                  serviceFG.get("info").value.sap_code === service.sap_code
              )
              .forEach((serviceFG) => {
                serviceFG.get("amount").setValue(0);
              });
          }}
        ></VolanteAdditionalServicesCart>
      </Sidebar>
      <Container>
        <Authorization
          actions={[
            "dam.get_dam_estimate",
            "volante.update_volante_quotation_estimate",
          ]}
        >
          <div style={{ width: "100%" }}>
            <div className="num-vol" style={{ fontWeight: 600 }}>
              {volante.id}
            </div>
            <div className="name-import">
              <b>{importer}</b>
            </div>
            <div className="restrictions-background">
              <StatusCommon
                label={text}
                status={numberProblems === 0 ? "ok" : "problem"}
              />
            </div>
            <div className="service-additional">
              <b>Agregar servicio adicional</b>
            </div>
            <div className="search-name">
              <div style={{ width: "100%", display: "flex" }}>
                <WForm formConfig={formConfig} style={{ width: "100%" }}>
                  <div style={{ display: "grid" }}>
                    <div
                      className="p-grid"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <div className="p-md-10">
                        <WFormControl name="input">
                          <TemplateInputContainer>
                            <WInputText
                              width="100%"
                              placeholder="Buscar por nombre"
                            />
                          </TemplateInputContainer>
                        </WFormControl>
                      </div>
                      <div
                        className="p-md-2"
                        style={{
                          display: "flex",
                          width: "9rem",
                          justifyContent: "flex-end",
                          height: "100%",
                          alignItems: "flex-end",
                        }}
                      >
                        <Button
                          type="submit"
                          className="buttonleft border-color"
                          icon="pi pi-search"
                        ></Button>
                      </div>
                    </div>
                  </div>
                </WForm>
              </div>
            </div>
            <div
              style={{
                fontFamily: "Montserrat",
                fontSize: 16,
                color: "#30345C",
                fontStyle: "normal",
                marginBottom: 10,
              }}
            >
              <VolanteAdditionalServicesTable
                estimateStatus={isSaving ? "PROCESSING" : ""}
                searchAdditionalServices={searchAdditionalServices}
                tableFormGroup={tableFormGroup}
              ></VolanteAdditionalServicesTable>
              <div className="add-service">
                {searchAdditionalServices.isSuccess && (
                  <ButtonAditionUp
                    style={{
                      height: 56,
                      marginRight: 18,
                      opacity: isInvalid || isSaving ? 0.5 : 1,
                    }}
                    disabled={isInvalid || isSaving}
                    onClick={async () => {
                      setIsSaving(true);
                      setSaveError("");
                      try {
                        await props.onSave(additionalServicesForSave);
                      } catch {
                        setSaveError(
                          "No se pudieron agregar los servicios. Inténtelo nuevamente."
                        );
                      } finally {
                        setIsSaving(false);
                      }
                    }}
                  >
                    {isSaving && (
                      <img
                        alt="Processing request"
                        height="50"
                        src={LoadingSpinner}
                        width="50"
                      />
                    )}
                    <img src={IconServiceMore} alt="" />
                    <b style={{ marginLeft: "0.37rem" }}>
                      Agregar servicios adicionales
                    </b>
                  </ButtonAditionUp>
                )}
                {shoppingCart.length > 0 && (
                  <IconNotification
                    icon={IconCart}
                    onClickIcon={() => {
                      setShowConfirmationModal(true);
                    }}
                    countNotification={shoppingCart.length}
                  ></IconNotification>
                )}
              </div>
              {saveError && (
                <p
                  style={{
                    color: "red",
                    fontSize: "1.2rem",
                    marginTop: "0.5rem",
                  }}
                >
                  {saveError}
                </p>
              )}
            </div>
          </div>
        </Authorization>
      </Container>
    </div>
  );
};
